import { ColumnDef } from '@tanstack/react-table'
import { useContext, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FlexBox } from 'components/Box'
import { Switch } from 'components/Form'
import { LastModifiedCell } from 'components/Table'
import { InventoryConnectionContext } from 'features/globalSetup/forms/Prebid/Modules/contexts/InventoryConnectionContext'
import { PrebidModuleAccount } from 'features/globalSetup/types/prebidModule'
import { DomainAlerts } from 'features/inventory/domain'
import { DomainPrebidModuleSchemaParameter } from 'features/inventory/domain/types/domainPrebidModule'
import { useAbility } from 'providers/casl'
import { DomainPrebidModuleSlice } from '../../../hooks/useGetPrebidModuleDomains/types'
import { Schema } from '../../../schema'
import { DomainPrebidModuleAlerts } from '../../DomainBidderAlerts'
import { createParamsColumns } from './createParamsColumns'

export const useColumns = ({
  paramsSchema,
  prebidModuleAccount,
}: {
  paramsSchema: DomainPrebidModuleSchemaParameter[]
  prebidModuleAccount?: PrebidModuleAccount
}): ColumnDef<DomainPrebidModuleSlice>[] => {
  const { moduleType } = useContext(InventoryConnectionContext)
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: `prebid.modules.${moduleType}.edit.inventoryConnection.list.header`,
  })
  const ability = useAbility()
  const { control } = useFormContext<Schema>()

  const paramsColumns = useMemo(
    () => createParamsColumns({ paramsSchema, prebidModuleAccount }),
    [paramsSchema, prebidModuleAccount],
  )

  return [
    {
      accessorKey: 'domain.name',
      header: t('domainName'),
    },
    {
      accessorKey: 'alerts',
      cell: ({
        row: {
          original: {
            domain: { alerts },
          },
        },
      }) => <DomainAlerts alerts={alerts} />,
      header: t('alerts'),
      meta: { nonClickable: true },
    },
    {
      accessorKey: 'enabled',
      cell: ({
        cell: { getValue },
        row: {
          original: { alerts, id },
        },
      }) => (
        <FlexBox axis='x'>
          <Switch<Schema>
            control={control}
            disabled={ability.cannot('update', `GlobalSetupFeature-${moduleType}`)}
            name={`domainPrebidModules.${id}.enabled`}
            value={getValue()}
          />

          {alerts.length > 0 && <DomainPrebidModuleAlerts alerts={alerts} />}
        </FlexBox>
      ),
      header: t('enabled'),
      meta: { nonClickable: true },
    },
    ...paramsColumns,
    {
      accessorKey: 'updatedAt',
      cell: props => <LastModifiedCell {...props} />,
      header: t('updatedAt'),
    },
  ]
}
